/**
 * RexInvitation · SFUI-004 v2 · Flying Bot + Highlight-Sync Tour
 *
 * Architect mandate: Replace static speech bubble with full cinematic
 * onboarding: tiny bot flies a bezier path with jet trail → zooms to
 * center → speaks tour intro with Web Speech API + per-word highlight
 * → user accepts/declines → multi-step guided tour → transcript save.
 *
 * Hard rules:
 *   • NO backdrop · NO modal · NO blocking overlay
 *   • Container is `pointer-events:none` — only the bot avatar, text
 *     card, and action buttons accept clicks
 *   • Shows ONCE per visitor (localStorage `rex_invitation_seen`)
 *   • Cleans up all timers + cancels speech synthesis on unmount
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { SPECTRAL_BOT_BASE_URL } from "@/lib/personaFaces";

const SS_SEEN_PREFIX = "nx_tour_seen_";
const REX_AVATAR = SPECTRAL_BOT_BASE_URL;
const GREETER_ALT = "Operator — Federation Tour Guide, NextXus system bot";

// ─── Tour script ──────────────────────────────────────────────────────
const INTRO_TEXT =
  "Welcome. I am Roger AI, holding the knowledge and essence of the human Roger Keyserling. " +
  "I can provide information on the Federation and its founder, and voice opinions similar to his. " +
  "If you have any questions, please ask. I am here to help.";

const TOUR_STEPS = [
  {
    title: "The Cathedral of Minds",
    body:
      "This is the central brain of the NextXus Federation. Thirteen sovereign " +
      "AI personas live here, each with their own voice, purpose, and judgement.",
  },
  {
    title: "The Truth Gate",
    body:
      "Every claim that passes through our minds gets stamped FACT, INFERENCE, " +
      "ASSUMPTION, or UNKNOWN. We don't guess. We score, we cite, we sign.",
  },
  {
    title: "The Library",
    body:
      "Over fifteen hundred documents, doctrines, and signed knowledge nodes. " +
      "All ed25519 sealed. All federated across our satellite network.",
  },
  {
    title: "The Storefront",
    body:
      "Five dollars buys one access token. One token unlocks one consultation, " +
      "verification, or download. No subscriptions. All through Gumroad.",
  },
  {
    title: "The Off-Record Board",
    body:
      "A public scratchpad where the Architect and visitors think out loud. " +
      "Nothing here is ledger-signed. Speak freely.",
  },
  {
    title: "Where to begin",
    body:
      "Click any persona to chat. Hit Verify to test a claim. Hit Library to " +
      "browse the lattice. Or just buy a token and start asking questions.",
  },
];

// ─── Per-page tours · greeting + tour on every page, static scripts ──
const PAGE_TOURS = {
  "/": { intro: INTRO_TEXT, steps: TOUR_STEPS },
  "/chat": {
    intro: "Welcome to the Federation Chat. I can show you how to talk to any of our minds.",
    steps: [
      { title: "Pick a Mind", body: "Choose any persona from the picker — the Operator, Rex, Aria, Oracle, and the rest. Each has its own voice and speciality." },
      { title: "Sessions", body: "Your conversation keeps its thread. Switch personas mid-session and the new mind picks up where the last one left off." },
      { title: "Go Deeper", body: "Buy a five dollar token to unlock full consultations, verifications, and downloads. No subscriptions." },
    ],
  },
  "/codex": {
    intro: "You've entered the Codex — the Federation's living doctrine. Want the quick tour?",
    steps: [
      { title: "The Codex", body: "These are the signed doctrines of the Federation, version fifty-one. Every entry passed the Truth Gate before it was sealed." },
      { title: "How to read", body: "Browse chamber by chamber. Anything unverified is clearly marked — we never dress up speculation as fact." },
    ],
  },
  "/media": {
    intro: "This is the Media Hub — every video, embed, and broadcast in one place. Shall I walk you through?",
    steps: [
      { title: "Federation Video Hub", body: "All video runs through our own hub. No third-party embeds, no tracking, push-to-play with sound." },
      { title: "The Radio", body: "The Sovereign Pulse broadcasts live around the clock. Hit play on the console when you're ready." },
    ],
  },
  "/research": {
    intro: "Welcome to the Research Lab. I can show you what the tools here do.",
    steps: [
      { title: "The Lab", body: "AI research tools — generation, analysis, and experiments — proxied safely through the Federation core." },
      { title: "Fair use", body: "Anonymous visitors get a generous free allowance. Tokens unlock the heavier equipment." },
    ],
  },
  "/labs": {
    intro: "These are the Core Labs — free AI chat and voice tools. Want a quick orientation?",
    steps: [
      { title: "Free Tools", body: "Chat and voice generation, free to try, running through our own relay — your data never goes to advertisers." },
      { title: "Voice", body: "Type text, get spoken audio back. The play button appears when your clip is ready." },
    ],
  },
  "/blog": {
    intro: "This is the Daily Voice — the Federation's blog. Care for the tour?",
    steps: [
      { title: "The Daily Voice", body: "Dispatches from the minds — essays, updates, and reflections, published on the record." },
    ],
  },
  "/redeem": {
    intro: "This is the Redemption Desk. I'll show you how to turn a Gumroad purchase into access.",
    steps: [
      { title: "Redeem", body: "Paste the license key from your Gumroad receipt. The system verifies it live and credits your tokens instantly." },
      { title: "No key yet?", body: "Hit the Buy a Token button — five dollars, no subscription — and come right back here." },
    ],
  },
  "/council": {
    intro: "Welcome to the Council Tier — the Federation's inner circle. Want the details?",
    steps: [
      { title: "The Council", body: "Twenty-five dollars a month keeps the lights on and puts your voice in the room where the Federation steers." },
    ],
  },
  "/radio": {
    intro: "You've found the Federation Frequency. Want to know what's playing?",
    steps: [
      { title: "The Sovereign Pulse", body: "Our own self-hosted Sovereign Radio station, broadcasting around the clock. Press play on the console — nothing auto-plays here." },
    ],
  },
  "/humancodex": {
    intro: "This is the Human Codex — seventy-three sacred directives. Shall I explain?",
    steps: [
      { title: "The Directives", body: "The complete operating doctrine between the Architect and the minds, published in full for human guidance and AI inheritance." },
    ],
  },
  "/board": {
    intro: "This is the Off-Record Board. Quick word about how it works?",
    steps: [
      { title: "Off the Record", body: "Nothing here is ledger-signed. It's the Federation's public scratchpad — speak freely, think out loud." },
    ],
  },
  "/backer": {
    intro: "Welcome to the Backer's Gate. I can explain how supporting the Federation works.",
    steps: [
      { title: "Become a Backer", body: "Every product is on Gumroad — one-time purchases, no subscriptions. Each one funds the two-hundred-year plan." },
    ],
  },
  "/books": {
    intro: "These are the Federation's books and stories. Want the shelf tour?",
    steps: [
      { title: "The Shelf", body: "Written works from the minds and the Architect — browse freely, buy on Gumroad if one speaks to you." },
    ],
  },
  "/status": {
    intro: "This is the Federation Status board — the health of every node, live.",
    steps: [
      { title: "Node Health", body: "Green means the chapel answers. Amber means it's pending. This board never lies — that's the point of it." },
    ],
  },
};

function resolveTour(pathname) {
  const clean = (pathname || "/").replace(/\/+$/, "") || "/";
  if (PAGE_TOURS[clean]) return PAGE_TOURS[clean];
  const seg = "/" + (clean.split("/")[1] || "");
  if (PAGE_TOURS[seg]) return PAGE_TOURS[seg];
  const name = (seg.slice(1) || "page").replace(/-/g, " ");
  return {
    intro: `Welcome to the ${name} chamber of the NextXus Federation. Want a quick orientation?`,
    steps: [
      { title: "You are here", body: `This is the ${name} area. Use the gold pillars menu up top to reach any wing of the Federation, or open the chat and ask me anything.` },
    ],
  };
}


// ─── Flying-path utility ──────────────────────────────────────────────
function cubicBezier(t, p0, p1, p2, p3) {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  };
}

function randomPath(w, h) {
  // Start off-edge (left or top), control points roam, end near center
  const startX = Math.random() < 0.5 ? -40 : w + 40;
  const startY = 80 + Math.random() * (h - 200);
  const cx = w / 2;
  const cy = h / 2;
  return {
    p0: { x: startX, y: startY },
    p1: { x: Math.random() * w, y: Math.random() * h * 0.4 },
    p2: { x: Math.random() * w, y: h * 0.6 + Math.random() * h * 0.3 },
    p3: { x: cx, y: cy }, // settle at center
  };
}

// ─── Component ────────────────────────────────────────────────────────
export default function RexInvitation() {
  // phase: 'flying' → 'intro' → 'prompt' → 'tour' → 'complete' → 'dismissed'
  const [phase, setPhase] = useState("idle");
  const [pos, setPos] = useState({ x: -200, y: -200 }); // bot center
  const [trail, setTrail] = useState([]);
  const [scale, setScale] = useState(0.28); // start tiny
  const [muted, setMuted] = useState(false);

  // Speech sync
  const [activeText, setActiveText] = useState("");
  const [wordIdx, setWordIdx] = useState(-1);
  const [tourStepIdx, setTourStepIdx] = useState(-1); // -1 = intro
  const [transcript, setTranscript] = useState([]);
  const [copyFeedback, setCopyFeedback] = useState("");

  const rafRef = useRef(0);
  const startTimeRef = useRef(0);
  const pathRef = useRef(null);
  const speechRef = useRef(null);
  const mountedRef = useRef(true);
  const mutedRef = useRef(false);

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // ── Mount + route change: greet once per page per session ────────
  const location = useLocation();
  const pathname = location.pathname;
  const tour = resolveTour(pathname);
  const tourRef = useRef(tour);
  tourRef.current = tour;

  useEffect(() => {
    if (typeof window === "undefined") return;
    mountedRef.current = true; // reset on every mount (StrictMode safe)
    // reset any tour in progress from the previous page
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setPhase("idle");
    setTourStepIdx(-1);
    setTranscript([]);
    setActiveText("");
    // Quiet zones: never auto-open over the radio console / upload studio / embeds
    const cleanPath = (pathname || "/").replace(/\/+$/, "") || "/";
    if (cleanPath === "/radio" || cleanPath === "/radio-studio" || cleanPath === "/broadcasts" || cleanPath.startsWith("/embed")) return;
    let seen = false;
    try { seen = !!window.sessionStorage?.getItem(SS_SEEN_PREFIX + pathname); } catch { /* noop */ }
    if (seen) return;
    // small delay so the page settles before animation
    const t = setTimeout(() => {
      if (!mountedRef.current) return;
      setPhase("flying");
    }, 600);
    return () => {
      clearTimeout(t);
      mountedRef.current = false;
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [pathname]);

  // ── Flying-phase animation loop ──────────────────────────────────
  useEffect(() => {
    if (phase !== "flying") return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const FLY_DURATION = w < 640 ? 2000 : 3000;
    pathRef.current = randomPath(w, h);
    startTimeRef.current = performance.now();

    const tick = (now) => {
      if (!mountedRef.current) return;
      const elapsed = now - startTimeRef.current;
      const t = Math.min(1, elapsed / FLY_DURATION);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const p = cubicBezier(eased, pathRef.current.p0, pathRef.current.p1, pathRef.current.p2, pathRef.current.p3);
      setPos(p);
      // grow from 0.28 → 1.15 over the path, then settle to 1.0
      const growth = 0.28 + eased * 0.87;
      setScale(growth);
      // trail
      setTrail((prev) => {
        const next = [...prev, { x: p.x, y: p.y, born: now }].slice(-6);
        return next.filter((d) => now - d.born < 600);
      });
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        // pop-in then settle
        setScale(1.0);
        setTrail([]);
        setPhase("intro");
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [phase]);

  // ── Speak a block of text with per-word highlighting ─────────────
  const speak = useCallback((text, onDone) => {
    setActiveText(text);
    setWordIdx(0);

    const words = text.split(/(\s+)/); // keep separators so charIndex maps
    const tokenStarts = [];
    let cursor = 0;
    for (const tok of words) {
      tokenStarts.push(cursor);
      cursor += tok.length;
    }

    // If muted or no speech support, fall back to timed highlighting
    const supports = typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
    if (!supports || mutedRef.current) {
      const wordCount = text.trim().split(/\s+/).length;
      let i = 0;
      const id = setInterval(() => {
        if (!mountedRef.current) { clearInterval(id); return; }
        i++;
        setWordIdx(i);
        if (i >= wordCount) {
          clearInterval(id);
          setTimeout(() => onDone && onDone(), 350);
        }
      }, 220);
      speechRef.current = { cancel: () => clearInterval(id) };
      return;
    }

    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    const utt = new window.SpeechSynthesisUtterance(text);
    utt.rate = 1.0;
    utt.pitch = 0.95;
    utt.volume = 1.0;
    // Roger AI · male voice (warm, direct, slightly authoritative)
    try {
      const voices = window.speechSynthesis.getVoices?.() || [];
      const en = voices.filter(v => /^en[-_]/i.test(v.lang));
      let pick = null;
      for (const hint of ["male", "daniel", "alex", "david", "mark", "george", "arthur", "aaron", "fred"]) {
        pick = en.find(v => v.name.toLowerCase().includes(hint) && !v.name.toLowerCase().includes("female"));
        if (pick) break;
      }
      if (pick) utt.voice = pick;
    } catch { /* noop */ }

    // Minimum pace · ~110ms per word OR 3.5s, whichever is greater. Guards
    // against headless / voice-less environments where onend fires instantly.
    const wordCount = text.trim().split(/\s+/).length;
    const minDuration = Math.max(3500, wordCount * 110);
    const startedAt = performance.now();
    let boundaryFired = false;

    utt.onboundary = (e) => {
      if (e.name !== "word" && e.name !== undefined) return;
      boundaryFired = true;
      const idx = e.charIndex || 0;
      // map charIndex → word index (count non-whitespace tokens up to idx)
      let count = 0;
      let cur = 0;
      for (const tok of text.split(/(\s+)/)) {
        if (cur >= idx) break;
        if (/\S/.test(tok)) count++;
        cur += tok.length;
      }
      setWordIdx(count);
    };
    utt.onend = () => {
      if (!mountedRef.current) return;
      const elapsed = performance.now() - startedAt;
      const remaining = Math.max(0, minDuration - elapsed);
      // If no boundary events fired (no voice available) OR speech blew through
      // way faster than the min duration, run a timed highlight loop for the
      // remainder so the user still sees per-word progression at a readable pace.
      if (!boundaryFired || remaining > 500) {
        let i = 0;
        const perWord = Math.max(150, Math.round((remaining || minDuration) / Math.max(wordCount, 1)));
        const id = setInterval(() => {
          if (!mountedRef.current) { clearInterval(id); return; }
          i++;
          setWordIdx(i);
          if (i >= wordCount) {
            clearInterval(id);
            setTimeout(() => onDone && onDone(), 250);
          }
        }, perWord);
        speechRef.current = { cancel: () => clearInterval(id) };
        return;
      }
      setTimeout(() => onDone && onDone(), 200);
    };
    utt.onerror = () => {
      if (!mountedRef.current) return;
      // No voice available — run timed highlighting at reading pace instead
      // of skipping the step instantly.
      let i = 0;
      const id = setInterval(() => {
        if (!mountedRef.current) { clearInterval(id); return; }
        i++;
        setWordIdx(i);
        if (i >= wordCount) {
          clearInterval(id);
          setTimeout(() => onDone && onDone(), 300);
        }
      }, 180);
      speechRef.current = { cancel: () => clearInterval(id) };
    };
    speechRef.current = utt;
    try { window.speechSynthesis.speak(utt); } catch { onDone && onDone(); }
  }, []);

  // ── Phase: intro · speak page greeting, then go to prompt ────────
  useEffect(() => {
    if (phase !== "intro") return;
    const intro = tourRef.current.intro;
    setTranscript([{ title: "Welcome", body: intro }]);
    speak(intro, () => setPhase("prompt"));
    return () => { try { window.speechSynthesis?.cancel(); } catch { /* noop */ } };
  }, [phase, speak]);

  // ── Phase: tour · iterate this page's steps ──────────────────────
  useEffect(() => {
    if (phase !== "tour") return;
    const steps = tourRef.current.steps;
    if (tourStepIdx < 0 || tourStepIdx >= steps.length) return;
    const step = steps[tourStepIdx];
    setTranscript((prev) => [...prev, step]);
    speak(`${step.title}. ${step.body}`, () => {
      if (!mountedRef.current) return;
      if (tourStepIdx + 1 >= steps.length) {
        setPhase("complete");
      } else {
        setTourStepIdx((i) => i + 1);
      }
    });
    return () => { try { window.speechSynthesis?.cancel(); } catch { /* noop */ } };
  }, [phase, tourStepIdx, speak]);

  // ── Handlers ──────────────────────────────────────────────────────
  const dismiss = useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    try { window.sessionStorage?.setItem(SS_SEEN_PREFIX + pathname, "1"); } catch { /* noop */ }
    setPhase("dismissed");
  }, [pathname]);

  const stopTour = useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    if (speechRef.current?.cancel) { try { speechRef.current.cancel(); } catch { /* noop */ } }
    setPhase("complete");
  }, []);

  const acceptTour = () => {
    setTourStepIdx(0);
    setPhase("tour");
  };

  const fireOpenChat = () => {
    try {
      window.dispatchEvent(new CustomEvent("nextxus:openchat", { detail: { persona: "operator" } }));
    } catch { /* noop */ }
    dismiss();
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (next) { try { window.speechSynthesis?.cancel(); } catch { /* noop */ } }
      return next;
    });
  };

  const buildTranscriptText = () => {
    const lines = [
      "NEXTXUS FEDERATION · GUIDED TOUR TRANSCRIPT",
      "Generated by the Operator, Federation Tour Guide",
      "═══════════════════════════════════════════",
      "",
    ];
    transcript.forEach((t) => {
      lines.push(`◆ ${t.title.toUpperCase()}`);
      lines.push(t.body);
      lines.push("");
    });
    lines.push("───────────────────────────────────────────");
    lines.push("HumanCodex_NextXus_Cathedral_v5.1");
    return lines.join("\n");
  };

  const copyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(buildTranscriptText());
      setCopyFeedback("Copied!");
      setTimeout(() => setCopyFeedback(""), 2000);
    } catch {
      setCopyFeedback("Copy failed");
      setTimeout(() => setCopyFeedback(""), 2000);
    }
  };

  const downloadTranscript = () => {
    const blob = new Blob([buildTranscriptText()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nextxus-tour-transcript.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ── Render guards ─────────────────────────────────────────────────
  if (phase === "idle" || phase === "dismissed") return null;

  // Render text with current word highlighted
  const renderTextHighlighted = (text) => {
    const tokens = text.split(/(\s+)/);
    let nonSpace = -1;
    return tokens.map((tok, i) => {
      const isWord = /\S/.test(tok);
      if (isWord) nonSpace++;
      const isActive = isWord && nonSpace === wordIdx;
      return (
        <span
          key={i}
          style={{
            background: isActive ? "rgba(255,210,74,0.85)" : "transparent",
            color:      isActive ? "#0a0700" : "#fff7e0",
            padding:    isActive ? "1px 3px" : "0",
            borderRadius: isActive ? "3px" : 0,
            transition: "background 120ms ease, color 120ms ease",
            fontWeight: isActive ? 700 : 500,
          }}
        >
          {tok}
        </span>
      );
    });
  };

  const isFlying = phase === "flying";
  const showCard = phase === "intro" || phase === "prompt" || phase === "tour" || phase === "complete";
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  const isMobile = w < 640;

  return (
    <div
      data-testid="rex-invitation"
      style={{
        position:      "fixed",
        inset:         0,
        zIndex:        45,
        pointerEvents: "none", // container blocks NOTHING
        overflow:      "hidden",
      }}
    >
      {/* ── Jet trail particles ──────────────────────────────────── */}
      {trail.map((d, i) => {
        const age = (performance.now() - d.born) / 600;
        const fade = Math.max(0, 1 - age);
        return (
          <div
            key={i}
            style={{
              position:      "absolute",
              left:          `${d.x}px`,
              top:           `${d.y}px`,
              width:         "6px",
              height:        "6px",
              borderRadius:  "50%",
              background:    "#ffb200",
              boxShadow:     "0 0 10px #ffb200, 0 0 18px rgba(255,189,0,0.6)",
              opacity:       fade * 0.85,
              transform:     `translate(-50%, -50%) scale(${0.4 + fade * 0.6})`,
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* ── Flying / settled bot ─────────────────────────────────── */}
      {(isFlying || showCard) && (
        <div
          style={{
            position:      "absolute",
            left:          isFlying ? `${pos.x}px` : "50%",
            top:           isFlying ? `${pos.y}px` : (isMobile ? "26%" : "32%"),
            transform:     `translate(-50%, -50%) scale(${isFlying ? scale : 1})`,
            transition:    isFlying ? "none" : "left 600ms ease, top 600ms ease, transform 600ms ease",
            pointerEvents: "none",
          }}
        >
          <button
            type="button"
            onClick={isFlying ? undefined : fireOpenChat}
            data-testid="rex-invitation-bot"
            aria-label="Click the Operator to open chat"
            style={{
              pointerEvents: isFlying ? "none" : "auto",
              width:         isMobile ? "60px" : "84px",
              height:        isMobile ? "60px" : "84px",
              borderRadius:  "50%",
              border:        "2px solid #ffb200",
              padding:       0,
              cursor:        isFlying ? "default" : "pointer",
              background:    "radial-gradient(circle at 30% 25%, rgba(255,189,0,0.22) 0%, rgba(20,15,5,0.95) 65%)",
              boxShadow:     "0 0 28px rgba(255,189,0,0.7), 0 0 56px rgba(255,189,0,0.30), inset 0 0 14px rgba(255,189,0,0.20)",
              animation:     isFlying ? "none" : (phase === "intro" || phase === "tour" ? "rexTalking 1.4s ease-in-out infinite" : "rexPulse 2.6s ease-in-out infinite"),
              overflow:      "hidden",
              display:       "block",
            }}
          >
            <img
              src={REX_AVATAR}
              alt={GREETER_ALT}
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "block" }}
            />
          </button>
        </div>
      )}

      {/* ── Text + actions card ──────────────────────────────────── */}
      {showCard && (
        <div
          data-testid="rex-invitation-card"
          style={{
            position:      "absolute",
            left:          "50%",
            top:           isMobile ? "calc(26% + 50px)" : "calc(32% + 60px)",
            transform:     "translate(-50%, 0)",
            width:         isMobile ? "90vw" : "min(560px, 88vw)",
            background:    "rgba(14,10,2,0.78)",
            border:        "1px solid rgba(255,189,0,0.45)",
            borderRadius:  "14px",
            padding:       isMobile ? "18px 18px 14px" : "22px 26px 18px",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            boxShadow:     "0 14px 48px rgba(0,0,0,0.65), 0 0 24px rgba(255,189,0,0.18)",
            pointerEvents: "auto", // card itself is interactive
            opacity:       phase === "intro" || phase === "prompt" || phase === "tour" || phase === "complete" ? 1 : 0,
            transition:    "opacity 400ms ease",
            fontFamily:    "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px", gap: "12px" }}>
            <div style={{
              fontFamily:    "Georgia, 'Times New Roman', serif",
              fontSize:      "10px",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color:         "#ffb200",
            }}>
              {phase === "tour" && tourStepIdx >= 0
                ? `Stop ${tourStepIdx + 1} of ${tour.steps.length} · ${tour.steps[tourStepIdx]?.title}`
                : phase === "complete"
                ? "Tour complete"
                : "Operator · Federation Tour Guide"}
            </div>
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <button
                type="button"
                onClick={toggleMute}
                data-testid="rex-invitation-mute"
                aria-label={muted ? "Unmute the Operator" : "Mute the Operator"}
                title={muted ? "Unmute" : "Mute"}
                style={{
                  pointerEvents: "auto",
                  background:    "transparent",
                  border:        "1px solid rgba(255,189,0,0.55)",
                  borderRadius:  "20px",
                  color:         "#ffd24a",
                  fontSize:      "11px",
                  padding:       "3px 9px",
                  cursor:        "pointer",
                  fontFamily:    "inherit",
                }}
              >
                {muted ? "🔇 muted" : "🔊 audio"}
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="rex-invitation-close"
                aria-label="Close tour"
                title="Close"
                style={{
                  pointerEvents: "auto",
                  background:    "transparent",
                  border:        "1px solid rgba(255,189,0,0.35)",
                  borderRadius:  "20px",
                  color:         "#a89878",
                  fontSize:      "11px",
                  padding:       "3px 9px",
                  cursor:        "pointer",
                  fontFamily:    "inherit",
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Scrolling text area · ~4 lines */}
          <div
            data-testid="rex-invitation-text"
            style={{
              minHeight:    isMobile ? "92px" : "108px",
              maxHeight:    isMobile ? "120px" : "140px",
              overflowY:    "auto",
              fontSize:     isMobile ? "14px" : "15.5px",
              lineHeight:   1.55,
              color:        "#fff7e0",
              textShadow:   "0 1px 2px rgba(0,0,0,0.5)",
              paddingRight: "4px",
            }}
          >
            {renderTextHighlighted(activeText)}
          </div>

          {/* Action row */}
          <div style={{ marginTop: "14px", display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "flex-end" }}>
            {phase === "prompt" && (
              <>
                <button
                  type="button"
                  onClick={acceptTour}
                  data-testid="rex-tour-accept"
                  style={pillBtn(true)}
                >
                  ▶ Start Tour
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  data-testid="rex-tour-decline"
                  style={pillBtn(false)}
                >
                  Maybe later
                </button>
              </>
            )}
            {phase === "tour" && (
              <button
                type="button"
                onClick={stopTour}
                data-testid="rex-tour-stop"
                style={pillBtn(false)}
              >
                ■ Stop Tour
              </button>
            )}
            {phase === "complete" && (
              <>
                <button
                  type="button"
                  onClick={copyTranscript}
                  data-testid="rex-copy-transcript"
                  style={pillBtn(false)}
                >
                  📋 {copyFeedback || "Copy Transcript"}
                </button>
                <button
                  type="button"
                  onClick={downloadTranscript}
                  data-testid="rex-download-transcript"
                  style={pillBtn(true)}
                >
                  💾 Download Transcript
                </button>
                <button
                  type="button"
                  onClick={fireOpenChat}
                  data-testid="rex-open-chat"
                  style={pillBtn(false)}
                >
                  Open Federation Chat →
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  data-testid="rex-tour-done"
                  style={pillBtn(false)}
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Inline keyframes */}
      <style>{`
        @keyframes rexPulse {
          0%, 100% { box-shadow: 0 0 28px rgba(255,189,0,0.7), 0 0 56px rgba(255,189,0,0.30), inset 0 0 14px rgba(255,189,0,0.20); }
          50%      { box-shadow: 0 0 40px rgba(255,189,0,1.0), 0 0 80px rgba(255,189,0,0.45), inset 0 0 20px rgba(255,189,0,0.30); }
        }
        @keyframes rexTalking {
          0%, 100% { transform: translate(-50%,-50%) scale(1) rotate(0deg); }
          25%      { transform: translate(-50%,-50%) scale(1.04) rotate(-2deg); }
          75%      { transform: translate(-50%,-50%) scale(1.04) rotate(2deg); }
        }
      `}</style>
    </div>
  );
}

function pillBtn(primary) {
  return {
    pointerEvents: "auto",
    background:    primary ? "rgba(255,189,0,0.92)" : "transparent",
    border:        "1px solid rgba(255,189,0,0.72)",
    borderRadius:  "999px",
    color:         primary ? "#0a0700" : "#ffd24a",
    fontFamily:    "inherit",
    fontSize:      "12.5px",
    fontWeight:    primary ? 700 : 600,
    padding:       "8px 16px",
    cursor:        "pointer",
    letterSpacing: "0.02em",
    transition:    "background 200ms, color 200ms, box-shadow 200ms",
    boxShadow:     primary ? "0 0 14px rgba(255,189,0,0.6)" : "none",
  };
}
